import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useDismiss, { DISMISS_REASON } from '@hooks/useDismiss';
import { getRovingTargetIndex, ROVING_KEY, ROVING_ORIENTATION } from '@hooks/useRovingFocus';

const MENU_ITEM_SELECTOR = '[role="menuitem"]:not(.disabled)';
const TOGGLE_FOCUS_SELECTOR = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const ITEM_ACTIVATION_KEYS = [ROVING_KEY.ENTER, ROVING_KEY.SPACE];
const KEYS_FROM_NOWHERE = { [ROVING_KEY.ARROW_DOWN]: ROVING_KEY.HOME, [ROVING_KEY.ARROW_UP]: ROVING_KEY.END };

const getMenuItems = (menu) => (menu ? [...menu.querySelectorAll(MENU_ITEM_SELECTOR)] : []);

const Dropdown = ({ 
    trigger, 
    children, 
    align = 'right',
    className = '',
    menuClassName = '',
    disabled = false,
    closeOnClick = true,
    keepOpenClass = false,
    usePortal = true,
    closeOnScroll = false,
    scrollContainerRef = null,
    onToggle
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [positionCalculated, setPositionCalculated] = useState(false);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);

    const updateMenuPosition = useCallback(() => {
        if (!dropdownRef.current || !isOpen) return;
        
        const triggerRect = dropdownRef.current.getBoundingClientRect();
        const menuWidth = menuRef.current?.offsetWidth || 200;
        const menuHeight = menuRef.current?.offsetHeight || 300;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        let left = triggerRect.left;
        let top = triggerRect.bottom;
        
        if (align === 'right') {
            left = triggerRect.right - menuWidth;
        }
        
        if (left + menuWidth > viewportWidth) {
            left = viewportWidth - menuWidth - 10;
        }
        
        if (left < 10) {
            left = 10;
        }
        
        if (top + menuHeight > viewportHeight) {
            const topAbove = triggerRect.top - menuHeight;
            if (topAbove > 10) {
                top = topAbove;
            } else {
                top = Math.max(10, viewportHeight - menuHeight - 10);
            }
        }
        
        setMenuPosition({ top, left });
        setPositionCalculated(true);
    }, [isOpen, align]);

    const openedByKeyboardRef = useRef(false);

    const focusToggle = useCallback(() => {
        const toggle = dropdownRef.current?.querySelector('.react-dropdown-toggle');
        toggle?.querySelector(TOGGLE_FOCUS_SELECTOR)?.focus();
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setPositionCalculated(false);
        onToggle?.(false);
    }, [onToggle]);

    const handleToggle = useCallback((event) => {
        if (disabled) return;
        const newState = !isOpen;
        // A click with detail 0 comes from Enter/Space on the trigger, not a pointer.
        openedByKeyboardRef.current = newState && event?.detail === 0;
        setIsOpen(newState);
        if (!newState) {
            setPositionCalculated(false);
        }
        onToggle?.(newState);
    }, [disabled, isOpen, onToggle]);

    useDismiss(dropdownRef, (event, reason) => {
        const focusWasInMenu = menuRef.current?.contains(document.activeElement);
        handleClose();
        if (reason === DISMISS_REASON.ESCAPE && focusWasInMenu) focusToggle();
    }, { enabled: isOpen, ignore: [menuRef] });

    useEffect(() => {
        if (!isOpen || !openedByKeyboardRef.current || (usePortal && !positionCalculated)) return;
        openedByKeyboardRef.current = false;
        getMenuItems(menuRef.current)[0]?.focus();
    }, [isOpen, usePortal, positionCalculated]);

    const handleMenuKeyDown = useCallback((event) => {
        if (event.key === 'Tab') {
            handleClose();
            focusToggle();
            event.preventDefault();
            return;
        }
        const menuItems = getMenuItems(event.currentTarget);
        const index = menuItems.indexOf(document.activeElement);
        const target = getRovingTargetIndex({
            key: index < 0 ? (KEYS_FROM_NOWHERE[event.key] ?? event.key) : event.key,
            index,
            count: menuItems.length,
            orientation: ROVING_ORIENTATION.VERTICAL,
        });
        if (target === null) return;
        event.preventDefault();
        menuItems[target]?.focus();
    }, [handleClose, focusToggle]);

    useLayoutEffect(() => {
        if (!isOpen || !usePortal) return;
        updateMenuPosition();
    }, [isOpen, usePortal, updateMenuPosition]);

    useEffect(() => {
        if (!isOpen || !usePortal || !positionCalculated) return;
        
        const handleScroll = () => updateMenuPosition();
        const handleResize = () => updateMenuPosition();
        
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen, usePortal, positionCalculated, updateMenuPosition]);

    useEffect(() => {
        if (!isOpen || !closeOnScroll) return;
        
        const scrollContainer = scrollContainerRef?.current || window;
        const handleScroll = () => handleClose();
        
        scrollContainer.addEventListener('scroll', handleScroll);
        
        return () => {
            scrollContainer.removeEventListener('scroll', handleScroll);
        };
    }, [isOpen, closeOnScroll, scrollContainerRef, handleClose]);


    const handleMenuClick = useCallback((event) => {
        if (event.target.closest('.react-dropdown-submenu')) {
            return;
        }
        
        if (closeOnClick && event.target.closest('li, .react-dropdown-item')) {
            handleClose();
        }
    }, [closeOnClick, handleClose]);

    const menuContent = isOpen && (
        <ul 
            ref={menuRef}
            className={classNames(
                'react-dropdown-menu', 
                menuClassName, 
                `react-dropdown-menu-${align}`, 
                { 
                    'show': isOpen,
                    'react-dropdown-menu-portal': usePortal,
                    'position-calculated': positionCalculated
                }
            )}
            style={usePortal ? {
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999,
                opacity: positionCalculated ? 1 : 0,
                visibility: positionCalculated ? 'visible' : 'hidden',
            } : undefined}
            role="menu" 
            onClick={handleMenuClick}
            onKeyDown={handleMenuKeyDown}
        >
            {children}
        </ul>
    );

    return (
        <>
            <div 
                className={classNames('react-dropdown', className, { 
                    'open': isOpen, 
                    'react-dropdown-visible': isOpen,
                    'keep-open': keepOpenClass 
                })} 
                ref={dropdownRef}
            >
                <div 
                    onClick={handleToggle} 
                    className="react-dropdown-toggle" 
                    role="button" 
                    aria-expanded={isOpen}
                    aria-disabled={disabled}
                >
                    {trigger}
                </div>
                {!usePortal && menuContent}
            </div>
            {usePortal && menuContent && createPortal(menuContent, document.body)}
        </>
    );
};


Dropdown.Item = ({ onClick, children, icon, className = '', disabled = false, href, dataAttributes }) => {
    const handleClick = (e) => {
        if (disabled) {
            e.preventDefault();
            return;
        }
        onClick?.(e);
    };

    // Enter on an anchor with href clicks natively; everything else is synthesised so the
    // click bubbles to the menu and closes it exactly as a pointer click does.
    const handleKeyDown = (e) => {
        if (!ITEM_ACTIVATION_KEYS.includes(e.key) || (href && e.key === ROVING_KEY.ENTER)) return;
        e.preventDefault();
        e.currentTarget.click();
    };

    const content = (
        <>
            {icon && <span className={icon} />}
            {children}
        </>
    );

    const dataAttrs = dataAttributes ? Object.fromEntries(dataAttributes) : {};

    return (
        <li className="react-dropdown-item-wrapper" role="none">
            <a 
                href={href} 
                className={classNames('react-dropdown-item', className, { 'disabled': disabled })} 
                role="menuitem"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                {...dataAttrs}
            >
                {content}
            </a>
        </li>
    );
};

Dropdown.Header = ({ children, className = '' }) => (
    <li className={classNames('react-dropdown-header', className)}>{children}</li>
);

Dropdown.Divider = ({ className = '' }) => (
    <li role="separator" className={classNames('react-dropdown-divider', className)} />
);

Dropdown.Submenu = ({ trigger, children, icon, className = '', disabled = false, align = 'right' }) => {
    const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const submenuRef = useRef(null);
    const submenuMenuRef = useRef(null);
    const timeoutRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (!submenuRef.current || !isSubmenuOpen) return;
        
        const triggerRect = submenuRef.current.getBoundingClientRect();
        const menuWidth = submenuMenuRef.current?.offsetWidth || 200;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        let left = triggerRect.right;
        let top = triggerRect.top;
        
        if (align === 'right' && left + menuWidth > viewportWidth) {
            left = triggerRect.left - menuWidth;
            
            if (left < 0) {
                left = viewportWidth - menuWidth - 10;
            }
        } else if (align === 'left') {
            left = triggerRect.left - menuWidth;
            
            if (left < 0) {
                left = triggerRect.right;
            }
        }
        
        const submenuHeight = submenuMenuRef.current?.offsetHeight || 300;
        if (top + submenuHeight > viewportHeight) {
            top = Math.max(10, viewportHeight - submenuHeight - 10);
        }
        
        setPosition({ top, left });
    }, [isSubmenuOpen, align]);

    const handleMouseEnter = useCallback(() => {
        if (disabled) return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsSubmenuOpen(true);
    }, [disabled]);

    const handleMouseLeave = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsSubmenuOpen(false);
        }, 200);
    }, []);

    const handleClick = useCallback((e) => {
        if (disabled) {
            e.preventDefault();
            return;
        }
        e.stopPropagation();
        setIsSubmenuOpen(!isSubmenuOpen);
    }, [disabled, isSubmenuOpen]);

    // Update position when submenu opens or on scroll/resize
    useEffect(() => {
        if (!isSubmenuOpen) return;
        
        updatePosition();
        
        const handleScroll = () => updatePosition();
        const handleResize = () => updatePosition();
        
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [isSubmenuOpen, updatePosition]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const submenuPortal = isSubmenuOpen && createPortal(
        <ul 
            ref={submenuMenuRef}
            className={classNames('react-dropdown-menu', 'react-dropdown-submenu-portal', `react-dropdown-menu-${align}`, { 'show': isSubmenuOpen })}
            role="menu"
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {children}
        </ul>,
        document.body
    );

    return (
        <li 
            className={classNames('react-dropdown-submenu', className, { 
                'open': isSubmenuOpen,
                'disabled': disabled 
            })}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            ref={submenuRef}
        >
            <a 
                href="#" 
                className={classNames('react-dropdown-item', { 'disabled': disabled })}
                onClick={handleClick}
                role="button"
                aria-expanded={isSubmenuOpen}
            >
                {icon && <span className={icon} />}
                {trigger}
                <span className="icon-svg-chevron-right react-submenu-arrow" />
            </a>
            {submenuPortal}
        </li>
    );
};

Dropdown.propTypes = {
    trigger: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    align: PropTypes.oneOf(['left', 'right']),
    className: PropTypes.string,
    menuClassName: PropTypes.string,
    disabled: PropTypes.bool,
    closeOnClick: PropTypes.bool,
    keepOpenClass: PropTypes.bool,
    usePortal: PropTypes.bool,
    closeOnScroll: PropTypes.bool,
    scrollContainerRef: PropTypes.object,
    onToggle: PropTypes.func,
};

Dropdown.Item.propTypes = {
    onClick: PropTypes.func,
    children: PropTypes.node.isRequired,
    icon: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    href: PropTypes.string,
    dataAttributes: PropTypes.arrayOf(PropTypes.array),
};

Dropdown.Header.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

Dropdown.Divider.propTypes = {
    className: PropTypes.string,
};

Dropdown.Submenu.propTypes = {
    trigger: PropTypes.node.isRequired,
    children: PropTypes.node.isRequired,
    icon: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    align: PropTypes.oneOf(['left', 'right']),
};

export default Dropdown;

